import { useState, useEffect } from 'react';
import { getBirds } from '../api/birds';
import { getSpecies, Species } from '../api/species';
import { CreateBreedingPairDto } from '../api/breeding';
import type { Bird } from '../api/birds';
import { Gender, BirdStatus } from '../utils/enums';
import { toast } from 'react-hot-toast';
import PairPredictionPreview from './genetics/PairPredictionPreview';
import CageSelect from './CageSelect';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from './ui/BrandModal';

interface AddBreedingPairModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (data: CreateBreedingPairDto) => void;
}

type FormData = {
  selectedSpecies: string;
  maleBird: string;
  femaleBird: string;
  pairingBeginDate: string;
  cageNumber: string;
  nestNumber: string;
  comments: string;
  allowHybridization: boolean;
};

export default function AddBreedingPairModal({ isOpen, onClose, onAdd }: AddBreedingPairModalProps) {
  const [birds, setBirds] = useState<Bird[]>([]);
  const [species, setSpecies] = useState<Species[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    selectedSpecies: '',
    maleBird: '',
    femaleBird: '',
    pairingBeginDate: new Date().toISOString().split('T')[0],
    cageNumber: '',
    nestNumber: '',
    comments: '',
    allowHybridization: false,
  });

  useEffect(() => {
    if (isOpen) {
      loadData();
    } else {
      // Reset form when modal closes
      setFormData({
        selectedSpecies: '',
        maleBird: '',
        femaleBird: '',
        pairingBeginDate: new Date().toISOString().split('T')[0],
        cageNumber: '',
        nestNumber: '',
        comments: '',
        allowHybridization: false,
      });
    }
  }, [isOpen]);

  // Reset bird selections when species changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      maleBird: '',
      femaleBird: ''
    }));
  }, [formData.selectedSpecies]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [birdsData, speciesData] = await Promise.all([
        getBirds(),
        getSpecies()
      ]);
      setBirds(birdsData);
      setSpecies(speciesData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      setFormData(prev => ({ ...prev, [name]: checkbox.checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.maleBird || !formData.femaleBird) return;

    const maleBird = birds.find(b => b.id === formData.maleBird);
    const femaleBird = birds.find(b => b.id === formData.femaleBird);

    if (!maleBird || !femaleBird) return;

    // Check if hybridization is allowed when different species are selected
    if (!formData.allowHybridization && maleBird.species?.id !== femaleBird.species?.id) {
      toast.error('Hybridization is not allowed. Please enable hybridization option or select birds from the same species.');
      return;
    }

    const requestData: CreateBreedingPairDto = {
      maleBirdId: maleBird.id,
      femaleBirdId: femaleBird.id,
      pairingBeginDate: formData.pairingBeginDate,
      cageNumber: formData.cageNumber || undefined,
      notes: formData.comments || undefined
    };

    try {
      onAdd(requestData);
      onClose();
      // Reset form
      setFormData({
        selectedSpecies: '',
        maleBird: '',
        femaleBird: '',
        pairingBeginDate: new Date().toISOString().split('T')[0],
        cageNumber: '',
        nestNumber: '',
        comments: '',
        allowHybridization: false,
      });
    } catch (error) {
      console.error('Failed to create breeding pair:', error);
    }
  };

  // Get available birds based on species filter
  const getFilteredBirds = (gender: Gender) => {
    let filteredBirds = birds.filter(bird => 
      bird.gender === gender && 
      bird.status === BirdStatus.AVAILABLE
    );

    // Filter by species if one is selected and hybridization is not allowed
    if (formData.selectedSpecies && !formData.allowHybridization) {
      filteredBirds = filteredBirds.filter(bird => 
        bird.species?.id === formData.selectedSpecies
      );
    }

    return filteredBirds;
  };

  const maleBirds = getFilteredBirds(Gender.MALE);
  const femaleBirds = getFilteredBirds(Gender.FEMALE);

  // Get unique species that have available birds for breeding
  const availableSpecies = species.filter(sp => {
    const hasAvailableMales = birds.some(bird => 
      bird.species?.id === sp.id && 
      bird.gender === Gender.MALE && 
      bird.status === BirdStatus.AVAILABLE
    );
    const hasAvailableFemales = birds.some(bird => 
      bird.species?.id === sp.id && 
      bird.gender === Gender.FEMALE && 
      bird.status === BirdStatus.AVAILABLE
    );
    return hasAvailableMales && hasAvailableFemales;
  });

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-2xl">
      <BrandModalHeader title="Add Breeding Pair" onClose={onClose} />
      <form onSubmit={handleSubmit}>
        <BrandModalBody className="space-y-4">
                      <div>
                        <label htmlFor="selectedSpecies" className="block text-sm font-medium text-slate-700">
                          Species
                        </label>
                        <select
                          id="selectedSpecies"
                          name="selectedSpecies"
                          value={formData.selectedSpecies}
                          onChange={handleChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-cyan-500 sm:text-sm"
                        >
                          <option key="empty-option-1753461297276" value="">Select a species</option>
                          {availableSpecies.map((sp) => (
                            <option key={sp.id} value={sp.id}>
                              {sp.commonName}
                            </option>
                          ))}
                        </select>
                        <p className="mt-1 text-xs text-slate-500">
                          {formData.selectedSpecies 
                            ? 'Bird selection is filtered to this species (unless hybridization is enabled)'
                            : 'Select a species to filter available birds, or leave blank to show all birds'
                          }
                        </p>
                      </div>

                      <div>
                        <label htmlFor="maleBird" className="block text-sm font-medium text-slate-700">
                          Male Bird
                          {formData.selectedSpecies && !formData.allowHybridization && (
                            <span className="ml-2 text-xs text-slate-500">
                              (filtered by selected species)
                            </span>
                          )}
                        </label>
                        <select
                          id="maleBird"
                          name="maleBird"
                          value={formData.maleBird}
                          onChange={handleChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-cyan-500 sm:text-sm"
                          required
                        >
                          <option key="empty-option-1753461297276" value="">
                            {maleBirds.length === 0 
                              ? 'No available male birds' 
                              : 'Select a male bird'
                            }
                          </option>
                          {maleBirds.map((bird) => (
                            <option key={bird.id} value={bird.id}>
                              {bird.bandId} - {bird.species?.commonName || 'Unknown'} 
                              {bird.mutationName ? ` (${bird.mutationName})` : ''}
                            </option>
                          ))}
                        </select>
                        {formData.selectedSpecies && maleBirds.length === 0 && (
                          <p className="mt-1 text-xs text-amber-600">
                            No available male birds for the selected species. Try enabling hybridization or selecting a different species.
                          </p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="femaleBird" className="block text-sm font-medium text-slate-700">
                          Female Bird
                          {formData.selectedSpecies && !formData.allowHybridization && (
                            <span className="ml-2 text-xs text-slate-500">
                              (filtered by selected species)
                            </span>
                          )}
                        </label>
                        <select
                          id="femaleBird"
                          name="femaleBird"
                          value={formData.femaleBird}
                          onChange={handleChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-cyan-500 sm:text-sm"
                          required
                        >
                          <option key="empty-option-1753461297276" value="">
                            {femaleBirds.length === 0 
                              ? 'No available female birds' 
                              : 'Select a female bird'
                            }
                          </option>
                          {femaleBirds.map((bird) => (
                            <option key={bird.id} value={bird.id}>
                              {bird.bandId} - {bird.species?.commonName || 'Unknown'} 
                              {bird.mutationName ? ` (${bird.mutationName})` : ''}
                            </option>
                          ))}
                        </select>
                        {formData.selectedSpecies && femaleBirds.length === 0 && (
                          <p className="mt-1 text-xs text-amber-600">
                            No available female birds for the selected species. Try enabling hybridization or selecting a different species.
                          </p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="pairingBeginDate" className="block text-sm font-medium text-slate-700">
                          Pairing Date
                        </label>
                        <input
                          type="date"
                          name="pairingBeginDate"
                          id="pairingBeginDate"
                          value={formData.pairingBeginDate}
                          onChange={handleChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-cyan-500 sm:text-sm"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="cageNumber" className="block text-sm font-medium text-slate-700">
                          Cage Number
                        </label>
                        <CageSelect
                          id="cageNumber"
                          name="cageNumber"
                          value={formData.cageNumber}
                          onChange={(cageNumber) =>
                            handleChange({ target: { name: 'cageNumber', value: cageNumber } } as React.ChangeEvent<HTMLInputElement>)
                          }
                          required
                          placeholder="Search or add a cage number…"
                        />
                      </div>

                      <div>
                        <label htmlFor="nestNumber" className="block text-sm font-medium text-slate-700">
                          Nest Number
                        </label>
                        <input
                          type="text"
                          name="nestNumber"
                          id="nestNumber"
                          value={formData.nestNumber}
                          onChange={handleChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-cyan-500 sm:text-sm"
                        />
                      </div>

                      <div>
                        <label htmlFor="comments" className="block text-sm font-medium text-slate-700">
                          Comments
                        </label>
                        <textarea
                          id="comments"
                          name="comments"
                          rows={3}
                          value={formData.comments}
                          onChange={handleChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-cyan-500 sm:text-sm"
                        />
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center">
                          <input
                            id="allowHybridization"
                            name="allowHybridization"
                            type="checkbox"
                            checked={formData.allowHybridization}
                            onChange={handleChange}
                            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-cyan-500"
                          />
                          <label htmlFor="allowHybridization" className="ml-2 block text-sm text-slate-700">
                            Allow Hybridization
                          </label>
                        </div>
                        
                        {/* Species compatibility indicator */}
                        {formData.maleBird && formData.femaleBird && (
                          <div className="text-xs">
                            {(() => {
                              const maleBird = birds.find(b => b.id === formData.maleBird);
                              const femaleBird = birds.find(b => b.id === formData.femaleBird);
                              const sameSpecies = maleBird?.species?.id === femaleBird?.species?.id;
                              
                              if (sameSpecies) {
                                return (
                                  <div className="flex items-center text-green-600">
                                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    Same species pairing ({maleBird?.species?.commonName})
                                  </div>
                                );
                              } else {
                                return (
                                  <div className="flex items-center text-amber-600">
                                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    Cross-species pairing: {maleBird?.species?.commonName} × {femaleBird?.species?.commonName}
                                  </div>
                                );
                              }
                            })()}
                          </div>
                        )}
                        
                        {formData.allowHybridization && (
                          <p className="text-xs text-slate-600">
                            Hybridization allows pairing birds from different species. Ensure this is appropriate for your breeding program.
                          </p>
                        )}
                      </div>

                      <PairPredictionPreview
                        maleBirdId={formData.maleBird || undefined}
                        femaleBirdId={formData.femaleBird || undefined}
                        speciesId={
                          formData.selectedSpecies ||
                          birds.find((b) => b.id === formData.maleBird)?.species?.id
                        }
                        compact
                      />
        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            className="btn btn-secondary text-sm"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary text-sm"
            disabled={isLoading}
          >
            {isLoading ? 'Adding...' : 'Add Breeding Pair'}
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
} 